import { useState, useRef, useEffect } from 'react'
import TopBar from '../components/TopBar';
import { Box, Grid, Item, Button, useTheme, Typography, IconButton } from '@mui/material';
import { tokens } from "../theme";
import api from '../utils/api';
import RegexValidatedTextField from '../components/RegexValidatedTextField';
import NameIcon from '@mui/icons-material/DriveFileRenameOutline';
import PriceIcon from '@mui/icons-material/Sell';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';

import AdminProduct from '../components/AdminProduct';
import { fetchProducts, fetchUsers, deleteProduct } from '../utils/apiHelpers';
import UserElement from '../components/UserElement';

import FileUpload from '../components/FileUpload';

import imageCompression from 'browser-image-compression';

/**
 * Div containing the Admin View page for baxappen, where
 * you can add and remove products from the baxshop.
 * @returns div containing page
 */
function AdminView() {

  // Contains values in the text fields
  const [fieldValues, setFieldValues] = useState({
    productName: '',
    price: '',
    imagePath: '',
  })

  // Contains all products in the database
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);

  // Contains bool determining if the input fields are valid
  const [validity, setValidity] = useState({
    productName: false,
    price: false,
  });

  const [loading, setLoading] = useState(true);

  const [file, setFile] = useState();
  const fileInputRef = useRef();

  useEffect(() => {
    Promise.all([fetchProducts(), fetchUsers()])
      .then(([productsData, usersData]) => {
        setProducts(productsData);
        setUsers(usersData);
        console.log(usersData);
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
      });
  }, []);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const priceRegex = new RegExp("^([0-9]*[.])?[0-9]+$");
  const nameRegex = new RegExp("^.{1,255}$");

  const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];

  var productIndex = 0
  var userIndex = 0;

  const updateProductFunction = async () => {
    try {
      const products = await fetchProducts();
      setProducts(products);
    } catch (err) {
      console.error("Error updating product list:", err);
    }
  };

  const handleDelete = async (product) => {
    try {
      await deleteProduct(product);
      const updatedProducts = await fetchProducts();
      setProducts(updatedProducts);
    } catch (err) {
      console.error("Error deleting product:", err);
    }
  };

  // Define compression options
  const options = {
    maxSizeMB: 0.5, // Max size in MB (adjust based on needs)
    maxWidthOrHeight: 256, // Resize max width or height
    useWebWorker: true
  };

  /**
   * Uses the values in fieldValues and file to first upload the image to the server
   * and get its path. Then a post request is created to create a Product in the database
   * with filepath from file, and name and price from fieldValues.
   * Lastly, refresh all values and update products variable
   * @param {event} e 
   */
  async function handleAddingProduct(e) {
    e.preventDefault();

    const compressedFile = await imageCompression(file, options);

    const formData = new FormData();
    formData.append('image', compressedFile);

    try {
      // Upload file to server as image
      const res = await api.post('/upload_image', formData);
      const path = "images/" + res.data.filename;
      const productData = {
        ...fieldValues,
        imagePath: path
      };

      // Create product from fieldValues and image path
      await api.post('/add_product', productData);

      // Reset variables
      setFieldValues({ productName: '', price: '', imagePath: '' });
      setFile(undefined);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Reset the file input
      }

      // Fetch and refresh products
      const products = await fetchProducts();
      setProducts(products);
    } catch (err) {
      console.log(err);
    }
  }

  return (
    <div>
      <TopBar/>
      <Box display="flex" flexDirection="column" justifyContent="flex-start" rowGap="5px">
        <Typography
          variant="h3"
          color={colors.ceriseAccent[900]}
          fontWeight="medium" 
          sx={{m : "10px 0 10px 15px" }}
          >
          Lägg till produkt
        </Typography>

        {/* Product name field */}
        <Box display="flex" alignItems="flex-start">
          <NameIcon sx={{fontSize : 32, m: "10px 10px 0 10px"}}/>
          <RegexValidatedTextField
          id="productnamefield"
          label="Produktnamn"
          variant="outlined"
          regex={nameRegex}
          errorMessage="Produktnamn måste vara 0-255 tecken"
          value={fieldValues.productName}
          onValidChange={(value, isValid) => {
            setFieldValues(prev => ({ ...prev, productName: value }));
            setValidity(prev => ({ ...prev, productName: isValid }));
          }}
          sx={{width: "100%", m: "0 10px 0 0"}}
          />
        </Box>

        {/* Price field */}
        <Box display="flex" alignItems="flex-start">
          <PriceIcon sx={{fontSize : 32, m: "10px 10px 0 10px"}}/>
          <RegexValidatedTextField
            id="pricefield"
            label="Pris"
            variant="outlined"
            regex={priceRegex} // Accepts valid positive decimal numbers
            errorMessage="Pris måste vara en positiv siffra"
            value={fieldValues.price}
            onValidChange={(value, isValid) => {
              setFieldValues((prev) => ({ ...prev, price: value }));
              setValidity(prev => ({ ...prev, price: isValid }));
            }}
            sx={{width: "100%", m: "0 10px 0 0"}}
          />
        </Box>

        <Box display="flex" flexDirection="column" sx={{m: "0 0 5px 50px"}}>
                <FileUpload file={file} setFile={setFile} fileInputRef={fileInputRef} />
        </Box>

        {/* Add product button */}
        <Button
          variant="outlined"
          disabled={!(validity.productName && validity.price && file)}
          color={colors.gray[900]}
          sx={{width: "150px", m: "0 0 0 50px"}}
          onClick={handleAddingProduct}
        >
          <AddIcon/>Lägg till produkt
        </Button>
      </Box>

      {/* Display products in database */}
      <Box display="flex" alignItems="center" sx={{m: "10px 0 10px 0"}}>
        <IconButton color={colors.gray[900]} sx={{m: "0 0 0 10px"}} onClick={fetchProducts}>
          <RefreshIcon/>
        </IconButton>
        <Typography
          variant="h3"
          color={colors.ceriseAccent[900]}
          fontWeight="medium" 
          sx={{m : "0 0 0 10px" }}
          >
          Produkter i databasen:
        </Typography>
      </Box>

      {/* Creating an AdminProduct for each product in the database */}
      {
        !loading ?
        products.map((product) => {
          productIndex++;
          return (
            <AdminProduct
            key={product.id}
            product={product}
            productIndex={productIndex}
            updateProductFunction={updateProductFunction}
            onDelete={() => handleDelete(product)}
            />
          )
        })
        : null
      }
      {/* Användare */}
      <Typography
        variant="h3"
        color={colors.ceriseAccent[900]}
        fontWeight="medium" 
        sx={{m : "20px 0 10px 10px" }}
        >
        Användare:
      </Typography>
      <Grid container display="flex" justifyContent="flex-start">
       <Grid size={0.5}>
        <Typography variant="h6" color={colors.gray[100]} fontWeight="medium" sx={{m : "0 0 0 10px" }}>
            #
        </Typography>
        </Grid>
        <Grid size={3.5}>
          <Typography variant="h6" color={colors.gray[100]} fontWeight="medium" sx={{m : "0 0 0 10px" }}>
              Användarnamn
          </Typography>
        </Grid>
        <Grid size={5}>
          <Typography variant="h6" color={colors.gray[100]} fontWeight="medium" sx={{m : "0 0 0 10px" }}>
              Visningsnamn
          </Typography>
        </Grid>
        <Grid size={1}>
          <Typography variant="h6" color={colors.gray[100]} fontWeight="medium">
              Verify
          </Typography>
        </Grid>
        <Grid size={1}>
          <Typography variant="h6" color={colors.gray[100]} fontWeight="medium">
              Admin
          </Typography>
        </Grid>
      </Grid>
      {
        !loading ?
        users.map((user) => {
          userIndex++;
          return (
            <UserElement
            key={user.id}
            user={user}
            userIndex={userIndex}
            updateUserFunction={() => fetchUsers().then(data => setUsers(data))}
            />
          )
        })
        : null
      }
    </div>
  )
}

export default AdminView;